import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BUILD_METRICS } from "@/data/buildMetrics";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { useBooking } from "@/context/BookingContext";
import { trackEvent } from "@/utils/analytics";
import "./BuiltByAgentsPage.scss";

const PipelineStep: React.FC<{
  step: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  index: number;
}> = ({ step, icon, title, desc, index }) => (
  <div className="bba-pipeline-step" style={{ "--i": index } as React.CSSProperties}>
    <div className="bba-step-number">{step}</div>
    <div className="bba-step-icon">{icon}</div>
    <h3 className="bba-step-title">{title}</h3>
    <p className="bba-step-desc">{desc}</p>
  </div>
);

const PlanIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
    <rect x="9" y="3" width="6" height="4" rx="1" />
    <path d="M9 12h6M9 16h4" />
  </svg>
);

const BuildIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

const ReviewIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
  </svg>
);

const ShipIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const BuiltByAgentsPage: React.FC = () => {
  const { t } = useTranslation();
  const { openBooking } = useBooking();
  const { ref: metricsRef, isVisible: metricsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: pipelineRef, isVisible: pipelineVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: logRef, isVisible: logVisible } = useScrollAnimation({ threshold: 0.05 });
  const { ref: moatRef, isVisible: moatVisible } = useScrollAnimation({ threshold: 0.1 });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    trackEvent("built_by_agents_view");
  }, []);

  useDocumentMeta({
    title: t("builtByAgents.pageTitle"),
    description: t("builtByAgents.pageDescription"),
    canonical: "https://devbloom.dev/built-by-agents",
    ogTitle: t("builtByAgents.pageTitle"),
    ogDescription: t("builtByAgents.pageDescription"),
    ogType: "website",
  });

  const { summary, weekly, lastUpdated } = BUILD_METRICS;

  const pipelineSteps = [
    { key: "plan", icon: <PlanIcon /> },
    { key: "build", icon: <BuildIcon /> },
    { key: "review", icon: <ReviewIcon /> },
    { key: "ship", icon: <ShipIcon /> },
  ] as const;

  return (
    <main className="bba-page">
      {/* ── Hero ── */}
      <section className="bba-hero">
        <div className="container">
          <p className="bba-label">{t("builtByAgents.hero.badge")}</p>
          <h1 className="bba-hero-title">{t("builtByAgents.hero.title")}</h1>
          <p className="bba-hero-sub">{t("builtByAgents.hero.subtitle")}</p>
        </div>
      </section>

      {/* ── Metrics band ── */}
      <section className="bba-metrics-band" aria-label={t("builtByAgents.metrics.heading")}>
        <div className="container">
          <h2 className="bba-section-heading">{t("builtByAgents.metrics.heading")}</h2>
          <p className="bba-as-of">
            {t("builtByAgents.metrics.asOf", { date: lastUpdated })}
          </p>
          <div
            ref={metricsRef}
            className={`bba-stat-grid${metricsVisible ? " visible" : ""}`}
          >
            <div className="bba-stat" style={{ "--i": 0 } as React.CSSProperties}>
              <span className="bba-stat-value">{summary.featuresShipped}</span>
              <span className="bba-stat-label">{t("builtByAgents.metrics.featuresShipped")}</span>
            </div>
            <div className="bba-stat" style={{ "--i": 1 } as React.CSSProperties}>
              <span className="bba-stat-value">{summary.reposInProduction}</span>
              <span className="bba-stat-label">{t("builtByAgents.metrics.reposInProduction")}</span>
            </div>
            <div className="bba-stat" style={{ "--i": 2 } as React.CSSProperties}>
              <span className="bba-stat-value">{summary.prsMerged}</span>
              <span className="bba-stat-label">{t("builtByAgents.metrics.prsMerged")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pipeline explainer ── */}
      <section className="bba-pipeline">
        <div className="container">
          <h2 className="bba-section-heading">{t("builtByAgents.pipeline.heading")}</h2>
          <p className="bba-pipeline-sub">{t("builtByAgents.pipeline.subheading")}</p>
          <div
            ref={pipelineRef}
            className={`bba-pipeline-grid${pipelineVisible ? " visible" : ""}`}
          >
            {pipelineSteps.map(({ key, icon }, i) => (
              <PipelineStep
                key={key}
                step={String(i + 1).padStart(2, "0")}
                icon={icon}
                title={t(`builtByAgents.pipeline.${key}.title`)}
                desc={t(`builtByAgents.pipeline.${key}.desc`)}
                index={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Weekly ship log ── */}
      <section className="bba-weekly-log">
        <div className="container">
          <h2 className="bba-section-heading">{t("builtByAgents.weeklyLog.heading")}</h2>
          <div
            ref={logRef}
            className={`bba-log-list${logVisible ? " visible" : ""}`}
          >
            {weekly.map((w, i) => (
              <div
                key={w.weekOf}
                className="bba-log-row"
                style={{ "--i": i } as React.CSSProperties}
              >
                <time className="bba-log-week" dateTime={w.weekOf}>
                  {new Date(w.weekOf + "T12:00:00Z").toLocaleDateString(undefined, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </time>
                <div className="bba-log-chips">
                  <span className="bba-chip bba-chip--features">
                    {t("builtByAgents.weeklyLog.features", { count: w.featuresShipped })}
                  </span>
                  <span className="bba-chip">
                    {t("builtByAgents.weeklyLog.prs", { count: w.prsMerged })}
                  </span>
                  <span className="bba-chip">
                    {t("builtByAgents.weeklyLog.repos", { count: w.reposTouched })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Moat section ── */}
      <section className="bba-moat" ref={moatRef}>
        <div className={`container bba-moat-inner${moatVisible ? " visible" : ""}`}>
          <h2 className="bba-section-heading">{t("builtByAgents.moat.heading")}</h2>
          <p className="bba-moat-body">{t("builtByAgents.moat.body")}</p>
        </div>
      </section>

      {/* ── CTA band ── */}
      <section className="bba-cta-band">
        <div className="container bba-cta-inner">
          <h2>{t("builtByAgents.cta.heading")}</h2>
          <button
            className="btn btn-accent"
            onClick={() => {
              trackEvent("built_by_agents_cta_click");
              openBooking("built_by_agents_page");
            }}
          >
            {t("builtByAgents.cta.button")} <ArrowIcon />
          </button>
        </div>
      </section>
    </main>
  );
};

export default BuiltByAgentsPage;
